// Second facteur (TOTP) — l'appel du backend, et le QR.
//
// Le second facteur est ici plus important qu'ailleurs, et il faut savoir
// pourquoi : **la clé maîtresse est la racine de tout**. Qui détient le mot de
// passe déballe K, et avec K peut reprendre l'identité de n'importe quel appareil
// (leur état MLS est sauvegardé sous K — c'est ce qui rend la récupération
// possible). Un appareil n'est donc PAS un second facteur.
//
// Le vrai rempart devant le mot de passe, c'est celui-ci.

import qrcode from 'qrcode-generator';
import { api } from '../api/client';

/** Ce que le serveur remet à l'enrôlement — et une seule fois. */
export type TotpSetup = {
  /** Secret en base32, pour la saisie manuelle si le QR ne passe pas. */
  secret: string;
  /** URI `otpauth://` — c'est ce que le QR encode. */
  otpauthUri: string;
};

export function setupTwoFactor(token: string): Promise<TotpSetup> {
  return api<TotpSetup>('/auth/2fa/setup', { method: 'POST', token });
}

export function enableTwoFactor(token: string, totpCode: string): Promise<void> {
  return api('/auth/2fa/enable', { method: 'POST', token, body: { totpCode } });
}

/** Désactiver exige un code valide : un jeton de session volé n'y suffit pas. */
export function disableTwoFactor(token: string, totpCode: string): Promise<void> {
  return api('/auth/2fa/disable', { method: 'POST', token, body: { totpCode } });
}

/**
 * Rend l'URI `otpauth://` en QR, **dans la page**.
 *
 * Pas de service de génération d'images : le secret TOTP ne doit sortir d'ici
 * sous aucun prétexte, et la CSP interdit de toute façon tout hôte externe. Le
 * SVG est fabriqué localement et n'est jamais transmis.
 */
export function qrSvg(otpauthUri: string): string {
  // Type 0 = version choisie automatiquement selon la longueur ; correction 'M'
  // (~15 % de redondance), le compromis usuel pour un QR lu à l'écran.
  const qr = qrcode(0, 'M');
  qr.addData(otpauthUri);
  qr.make();
  return qr.createSvgTag({ cellSize: 5, margin: 4, scalable: true });
}

/** Le secret en groupes de 4 : c'est ainsi qu'il se recopie sans faute. */
export function groupSecret(secret: string): string {
  return secret.replace(/(.{4})/g, '$1 ').trim();
}
